import React, { useCallback, useEffect, useState } from 'react';
import ButtonsIncDec from '../ButtonsIncDec';
import { useLoginContext } from '../../../Contexts/LoginContext';
import { useCartContext } from '../../../Contexts/CartContext';
import { useAddProductContext } from '../../../Contexts/AddProductContext';
import DOMPurify from 'dompurify';
import Img from '../Img';
import ProductPrice from './ProductPrice';
import { TbCircleCheck, TbCircleCheckFilled, TbSearch } from 'react-icons/tb';
import Modal from '../Modal';
import Button from '../../Form/Button';
import { HiXMark } from 'react-icons/hi2';
import BadgeProductSale from './BadgeProductSale';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { vibrate } from '../../../Utils/Helpers';
import BadgeNewProduct from './BadgeNewProduct';

interface SlotStepProductProps {
  product: any;
  complement: any;
  className?: string;
  step?: any;
}

type TriggerInc = () => void;

const SlotStepProduct = ({
  product,
  complement,
  className,
  step

}: SlotStepProductProps) => {

  const { updateCurrentSteps, getTotalSelectedComplements, cartDisplay } = useCartContext();
  const { compositeProductBlocked } = useAddProductContext();
  const { isAuthenticated, setShowLoginStoreCounter } = useLoginContext();
  const [ triggerInc, setTriggerInc ] = useState<TriggerInc | null>(null);
  const [ enlargedProduct, setEnlargedProduct ] = useState<any | null>(null);
  const { isStoreCounter } = useOrderTypeContext();

  const handleClick = () => {
    if (!isAuthenticated && isStoreCounter) {
      setShowLoginStoreCounter(true);
      return;
    }
    if (step?.type === 'unica') {
      handleComplement();
    }
    triggerInc?.();
  }

  const handleIncOutside = useCallback((method: TriggerInc) => {
    setTriggerInc(() => method);
  }, []);

  const [ forcedQuantity, setForcedQuantity ] = useState<number | null>(null);
  const handleComplement = (quantity: number = 1) => {
    setForcedQuantity(quantity);
    updateCurrentSteps(step.id, complement.id, quantity, step.type)
    vibrate();
  }

  const [complementDescription, setComplementDescription] = useState('');

  useEffect(() => {
    setComplementDescription(DOMPurify.sanitize(enlargedProduct?.description));
  }, [enlargedProduct]);

  useEffect(() => {
    if (complement.selected) {
      setForcedQuantity(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sanitizeText = (text: string) => {
    return DOMPurify.sanitize(text);
  }

  return (
    <>
      <section className='relative flex items-center justify-between'>
        <label
          htmlFor={complement.id}
          onClick={handleClick}
          className={`
            flex items-center w-full px-4 py-3 gap-2 justify-between hover:ring-gray-200 hover:rounded-lg ring-inset
            active:ring-2 active:rounded-lg active:ring-custom-primary transition-all ease-in-out duration-200 cursor-pointer
            ${complement.image ? 'min-h-32' : ''}
            ${(
              (!cartDisplay && isStoreCounter)
              || (step.type === 'unica' && complement.quantity > 0)
              || (step.type === 'multipla' && getTotalSelectedComplements(step.id) >= step.quantity))
              && 'pointer-events-none'}
            ${className}
          `}
        >
          <div className={`grid gap-1 w-full ${(!isStoreCounter || cartDisplay) && !complement.image && 'pr-12'}`}>
            <h4 className='font-medium leading-4 text-shopfood-title'>{complement.name}</h4>
            {complement.description && <div className='text-sm leading-4 text-gray-400 w-full' dangerouslySetInnerHTML={{ __html: sanitizeText(complement.description) }} />}
            <div className='text-gray-500 flex items-center gap-1 mt-0.5 text-sm'>

              {complement.chargeable && <ProductPrice product={complement} />}

              {/* BADGE FOR NEW PRODUCT ON CATALOG */}
              <BadgeNewProduct product={complement} outline className='inline-block ml-1 rounded self-end -mb-0.5' />

              {/* PRODUCT SALE  */}
              {complement.chargeable && <BadgeProductSale product={complement} outline className='inline-block ml-1 rounded self-end -mb-0.5' />}
            </div>
          </div>

          {complement.image &&
            <div className={`${!isStoreCounter || (cartDisplay && !compositeProductBlocked) ? 'pr-12' : 'p-1'}`}>
              <Img src={complement.image}  alt={product.name}  className="
              min-w-16 min-h-16 max-w-16 max-h-16
              lg:min-w-20 lg:min-h-20 lg:max-w-20 lg:max-h-20
              object-contain rounded-lg cursor-pointer transition-all hover:opacity-80" />
            </div>
          }
        </label>

        {complement.image &&
            <div className={`absolute flex items-center text-xs text-gray-300 gap-0.5
                mt-[7.5em] md:mt-[8.5em] cursor-pointer
                ${!isStoreCounter || (cartDisplay && !compositeProductBlocked) ? 'right-16 mr-1.5' : 'right-6'}
            `} onClick={() => setEnlargedProduct(complement)}>
              <TbSearch size={13} />
              ZOOM
            </div>
        }

        {(!isStoreCounter || (isStoreCounter && cartDisplay)) && !compositeProductBlocked &&
          <div className="absolute right-4 flex items-center justify-center">
            {step.type === 'unica'
              ?
                complement.quantity > 0
                  ? <TbCircleCheckFilled size={30} className='mr-1 text-custom-primary cursor-pointer' onClick={() => handleComplement()} />
                  : <TbCircleCheck size={30} className='mr-1 text-gray-400 cursor-pointer' onClick={() => handleComplement()} />
              : null
            }
            {step?.type === 'multipla' &&
              <ButtonsIncDec
                onInc={handleComplement} onDec={handleComplement}
                quantityMin={0}
                disabledInc={getTotalSelectedComplements(step.id) >= step.quantity}
                showTrash
                className='absolute z-10 right-0 text-sm h-9'
                onClickIncOutside={handleIncOutside}
                forcedQuantity={forcedQuantity}
              />
            }
          </div>
        }
      </section>

      {enlargedProduct &&
        <Modal open={enlargedProduct} className="w-auto max-w-sm m-4" onClose={() => setEnlargedProduct(null)}>
          <div className='flex flex-col gap-2 justify-center items-center'>
            <Img src={enlargedProduct.image} alt={enlargedProduct.name} className='w-full rounded-t-xl' />

            <div className='flex gap-2 items-center justify-between w-full'>
              <div className='flex flex-col text-left w-full p-4 gap-2'>
                <span className='font-bold leading-5 text-shopfood-title'>{enlargedProduct.name}</span>
                {complementDescription && <span className='text-gray-500 leading-4 text-sm' dangerouslySetInnerHTML={{ __html: complementDescription }} />}

                <div className='flex items-center justify-between gap-2 mt-1'>
                  <div className='text-gray-500 font-medium flex items-center gap-1 mt-0.5'>
                    <ProductPrice product={enlargedProduct} />

                    {/* BADGE FOR NEW PRODUCT ON CATALOG */}
                    <BadgeNewProduct product={enlargedProduct} outline className='inline-block ml-1 rounded self-end -mb-0.5' />

                    {/* PRODUCT SALE  */}
                    <BadgeProductSale product={enlargedProduct} outline className='inline-block ml-1 rounded self-end -mb-0.5' />
                  </div>

                  <div className="flex gap-2 items-center justify-end">
                    {(step?.type === 'multipla' && (!isStoreCounter || (isStoreCounter && cartDisplay)) && !compositeProductBlocked) &&
                      <ButtonsIncDec
                        onInc={handleComplement} onDec={handleComplement}
                        quantityMin={0}
                        disabledInc={getTotalSelectedComplements(step.id) >= step.quantity}
                        showTrash
                        className='text-sm h-9'
                        forcedQuantity={forcedQuantity}
                      />
                    }
                    {(step.type === 'unica' && (!isStoreCounter || (isStoreCounter && cartDisplay)) && !compositeProductBlocked)
                      ?
                        complement.quantity > 0
                          ? <TbCircleCheckFilled size={45} className='text-custom-primary cursor-pointer' onClick={() => {handleComplement(); setEnlargedProduct(false);}} />
                          : <TbCircleCheck size={45} className='text-gray-400 cursor-pointer' onClick={() => {handleComplement(); setEnlargedProduct(false);}} />
                      : null
                    }
                    <Button onClick={() => setEnlargedProduct(false)} className={`w-9 h-9`}>
                      <div><HiXMark size={20} /></div>
                    </Button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </Modal>
      }
    </>
  )
}

export default SlotStepProduct;